import React, { useState } from 'react';
import {
  Box,
  Paper,
  Stack,
  Typography,
  IconButton,
  Collapse,
  Divider,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { getCategoryColor } from './categoryPalette';
import { getLandmarkMeta } from './landmarkIcons';

const MapLegend = ({ categories, landmarkKinds }) => {
  const [open, setOpen] = useState(false);

  return (
    <Paper
      elevation={3}
      sx={{
        position: 'absolute',
        bottom: 16,
        left: 16,
        maxWidth: 260,
        zIndex: 5,
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{
          px: 1.5,
          py: 1,
          cursor: 'pointer',
          '&:hover': { background: '#f5f5f5' },
        }}
        onClick={() => setOpen((o) => !o)}
      >
        <InfoOutlinedIcon fontSize="small" color="action" />
        <Typography variant="subtitle2" sx={{ fontWeight: 700, flex: 1 }}>
          Legend
        </Typography>
        <IconButton size="small" aria-label="toggle legend">
          {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
      </Stack>
      <Collapse in={open}>
        <Box sx={{ px: 1.5, pb: 1.5, maxHeight: 360, overflowY: 'auto' }}>
          {categories.length > 0 && (
            <>
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                Vendor categories
              </Typography>
              <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                {categories.map((cat) => (
                  <Stack key={cat.slug} direction="row" alignItems="center" spacing={1}>
                    <Box
                      sx={{
                        width: 14,
                        height: 14,
                        borderRadius: 0.75,
                        background: getCategoryColor(cat.slug),
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="caption" sx={{ flex: 1 }}>
                      {cat.name}
                    </Typography>
                    {cat.count != null && (
                      <Typography variant="caption" color="text.secondary">
                        {cat.count}
                      </Typography>
                    )}
                  </Stack>
                ))}
              </Stack>
            </>
          )}

          {landmarkKinds.length > 0 && (
            <>
              {categories.length > 0 && <Divider sx={{ my: 1 }} />}
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                On the map
              </Typography>
              <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                {landmarkKinds.map((kind) => {
                  const meta = getLandmarkMeta(kind);
                  const Icon = meta.Icon;
                  return (
                    <Stack key={kind} direction="row" alignItems="center" spacing={1}>
                      <Box
                        sx={{
                          width: 22,
                          height: 22,
                          borderRadius: '50%',
                          background: '#fff',
                          border: `2px solid ${meta.color}`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        <Icon fontSize="inherit" sx={{ color: meta.color, fontSize: 14 }} />
                      </Box>
                      <Typography variant="caption">{meta.label}</Typography>
                    </Stack>
                  );
                })}
              </Stack>
            </>
          )}

          <Divider sx={{ my: 1 }} />
          <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
            Other tiles
          </Typography>
          <Stack spacing={0.5} sx={{ mt: 0.5 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Box sx={{ width: 14, height: 14, borderRadius: 0.75, background: '#e8e2d1', border: '1px solid #b8a98a' }} />
              <Typography variant="caption">Open booth</Typography>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Box
                sx={{
                  width: 14,
                  height: 14,
                  borderRadius: 0.75,
                  backgroundImage:
                    'repeating-linear-gradient(45deg, #efe7d8 0 3px, #d8cdb6 3px 4px)',
                }}
              />
              <Typography variant="caption">Walkway</Typography>
            </Stack>
          </Stack>
        </Box>
      </Collapse>
    </Paper>
  );
};

export default MapLegend;
